import React, { useEffect, useMemo, useReducer, useState } from 'react';
import {
  AutocompleteComponent,
  DatePickerComponent,
  DialogComponent,
  Inputs,
  SelectComponet,
  SwitchComponent,
} from '../../../../../../../Components';
import {
  getErrorByName,
  GetParams,
  showError,
  showSuccess,
} from '../../../../../../../Helper';
import { offerSchema } from './JoiSchema';
import { useTranslation } from 'react-i18next';
import ServicesAutocomplete from './components/ServicesAutocomplete';
import { ImageUploader } from '../../../../../../../SharedComponents/ImageUploader/ImageUploader';
import LookupAutocomplete from '../../../../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import {
  CreateVendorOffer,
  UpdateVendorOffer,
} from '../../../../../../../Services';
import moment from 'moment';
import { mapActiveItemToState, mapStateToActiveItem } from './model';
import { useFetchVendorCategories } from '../../hooks/fetchVendorCategories';
import { useFetchVendors } from '../../hooks/fetchVendors';
import './OfferManagementDialog.scss';

const reducer = (state, action) => {
  if (action.id === 'edit') {
    return action.value;
  }
  if (action.id === 'edit-some') {
    return {
      ...state,
      ...action.value,
    };
  }
  state[action.id] = action.value;
  return { ...state };
};

const defaultState = () => ({
  offerName: '',
  vendorServiceId: null,
  vendorId: +GetParams('id'),
  offerStartDate: null,
  offerEndDate: null,
  offerWithDuration: false,
  offerDuration: null,
  description: '',
  discountPercentage: null,
  offerAvailableOn: null,
  promoCode: '',
  qrCode: '',
  qrFileId: null,
  offerStatusId: null,
  isPublishOffer: false,
  vendorServiceCategoryIds: [],
  selectedCategories: null,
  selectedService: null,
  discountType: null,
  vendorAgreementDetails: null,
  vendorAgreementId: null,
});

const offerAvailabilityOptions = [
  { title: 'Online', value: 1 },
  { title: 'On Site', value: 2 },
  { title: 'Both', value: 3 },
];

const OfferManagementDialog = ({
  parentTranslationPath,
  open,
  onClose,
  reload,
  activeItem,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const { categories, setVendorId, isLoading, associatedAgreements } =
    useFetchVendorCategories(defaultState().vendorId);
  const {
    vendors,
    getVendorsAdvanceSearch,
    isLoading: isLoadingVendors,
  } = useFetchVendors();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    ...defaultState(),
  });
  const schema = offerSchema(t).validate(state);
  const displayVendor = useMemo(() => {
    if (!defaultState().vendorId && !activeItem) return true;
    if (activeItem) return false;
    else return false;
  }, [activeItem]);

  const commissionTypeTranslation = (type) => {
    if (typeof type === 'number') {
      switch (type) {
        case 1:
          return t('Flat Rate (%)');
        case 2:
          return t('Fixed Commission (AED)');
        case 3:
          return t('Tiered Commission (Slab)');
        default:
          return t('Unknown Commission Type');
      }
    }
    return t(type);
  };

  const getDateValueForDisplay = (date) => {
    return moment(date).format('YYYY-MM-DD');
  };

  const saveHandler = async (event) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (schema.error) {
      showError(t('Shared:please-fix-all-errors'));
      return;
    }
    try {
      if (!activeItem) {
        await CreateVendorOffer(mapStateToActiveItem(state));
        showSuccess(t('offer_created_successfully'));
      } else {
        const updatedState = { ...mapStateToActiveItem(state) };
        delete updatedState.vendorAgreementDetails;
        await UpdateVendorOffer(activeItem.vendorOfferId, updatedState);
        showSuccess(t('offer_updated_successfully'));
      }
      reload();
      onClose();
    } catch (error) {
      showError(t('something_went_wrong'));
    }
  };
  const onCloseHandler = () => {
    dispatch({ id: 'edit', value: defaultState() });
    onClose();
  };
  useEffect(() => {
    if (activeItem) {
      dispatch({ id: 'edit', value: mapActiveItemToState(activeItem) });
    }
  }, [activeItem]);

  useEffect(() => {
    if (state.offerStartDate && state.offerEndDate) {
      dispatch({
        id: 'offerDuration',
        value: moment(state.offerEndDate).diff(state.offerStartDate, 'days'),
      });
    } else {
      dispatch({
        id: 'offerDuration',
        value: null,
      });
    }
  }, [state.offerStartDate, state.offerEndDate]);

  useEffect(() => {
    if (associatedAgreements?.length === 1) {
      const selectedAgreement = associatedAgreements[0];
      dispatch({
        id: 'edit-some',
        value: {
          vendorAgreementDetails: selectedAgreement,
          vendorAgreementId:
            selectedAgreement?.agreementID ||
            selectedAgreement?.vendorAgreementId,
        },
      });
    }
  }, [associatedAgreements]);

  return (
    <DialogComponent
      titleText={activeItem ? 'edit_offer' : 'add_offer'}
      parentTranslationPath={parentTranslationPath}
      isOpen={open}
      onCancelClicked={onCloseHandler}
      onCloseClicked={onCloseHandler}
      maxWidth={'sm'}
      dialogContent={
        <div className=' flex gap-1 d-flex-column'>
          <Inputs
            labelValue={'offer_name'}
            onInputChanged={(e) =>
              dispatch({ id: 'offerName', value: e.target.value })
            }
            value={state.offerName}
            parentTranslationPath={parentTranslationPath}
            isWithError
            labelClasses='Requierd-Color'
            error={getErrorByName(schema, 'offerName').error}
            helperText={getErrorByName(schema, 'offerName').message}
            isSubmitted={isSubmitted}
          />
          {displayVendor && (
            <AutocompleteComponent
              data={vendors}
              labelValue={'vendor'}
              onOpen={() => getVendorsAdvanceSearch()}
              multiple={false}
              displayLabel={(option) => option.name ?? ''}
              isLoading={isLoadingVendors}
              getOptionSelected={(option, value) => option.id === value.id}
              withoutSearchButton
              onChange={(_e, newValue) => {
                setVendorId(newValue?.id);
                dispatch({
                  id: 'edit-some',
                  value: {
                    vendorId: newValue?.id,
                    selectedCategories: null,
                    selectedService: null,
                    vendorServiceCategoryIds: [],
                    vendorServiceId: null,
                  },
                });
              }}
              parentTranslationPath={parentTranslationPath}
              onInputChange={(_e, value) => {
                getVendorsAdvanceSearch(value);
              }}
            />
          )}
          <AutocompleteComponent
            data={categories}
            labelValue={'service_category'}
            getOptionSelected={(option, value) =>
              option.serviceCategoryId === value.serviceCategoryId
            }
            isLoading={isLoading}
            isSubmitted={isSubmitted}
            labelClasses='Requierd-Color'
            parentTranslationPath={parentTranslationPath}
            selectedValues={state.selectedCategories}
            displayLabel={(option) => option.serviceCategoryName}
            chipsLabel={(option) => option.serviceCategoryName}
            withoutSearchButton
            multiple={false}
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  vendorServiceCategoryIds: newValue?.serviceCategoryId
                    ? [newValue?.serviceCategoryId]
                    : [],
                  selectedCategories: newValue,
                  selectedService: null,
                  vendorServiceId: null,
                },
              });
            }}
            isWithError
            error={getErrorByName(schema, 'vendorServiceCategoryIds').error}
            helperText={
              getErrorByName(schema, 'vendorServiceCategoryIds').message
            }
          />
          <ServicesAutocomplete
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  vendorServiceId: newValue?.vendorServiceId,
                  selectedService: newValue,
                },
              });
            }}
            isSubmitted={isSubmitted}
            vendorId={state.vendorId}
            selectedService={state.selectedService}
            selectedCategory={state.vendorServiceCategoryIds[0]}
            isWithError
            error={getErrorByName(schema, 'vendorServiceId').error}
            helperText={getErrorByName(schema, 'vendorServiceId').message}
          />
          <AutocompleteComponent
            data={associatedAgreements}
            labelValue={'associated-agreements'}
            isSubmitted={isSubmitted}
            labelClasses='Requierd-Color'
            selectedValues={state.vendorAgreementDetails}
            displayLabel={(option) =>
              `#${
                option.agreementID || option.vendorAgreementId
              } ${commissionTypeTranslation(option.psiCommissionType)} ${
                (typeof option.psiCommissionType === 'string' &&
                  option.psiCommissionType.toLowerCase() === 'slab') ||
                option.psiCommissionType === 3
                  ? ''
                  : '- %' + option.psiCommission
              } | ${getDateValueForDisplay(
                option.startDate || option.contractStartDate
              )} - ${getDateValueForDisplay(
                option.endDate || option.contractExpiryDate
              )}`
            }
            renderOption={(option) =>
              `#${
                option.agreementID || option.vendorAgreementId
              } ${commissionTypeTranslation(option.psiCommissionType)} ${
                (typeof option.psiCommissionType === 'string' &&
                  option.psiCommissionType.toLowerCase() === 'slab') ||
                option.psiCommissionType === 3
                  ? ''
                  : '- %' + option.psiCommission
              } | ${getDateValueForDisplay(
                option.startDate || option.contractStartDate
              )} - ${getDateValueForDisplay(
                option.endDate || option.contractExpiryDate
              )}`
            }
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  vendorAgreementDetails: newValue,
                  vendorAgreementId:
                    newValue?.agreementID || newValue?.vendorAgreementId,
                },
              });
            }}
            isDisabled={associatedAgreements.length === 1}
            isWithError
            multiple={false}
            withoutSearchButton
            isLoading={isLoading}
            error={getErrorByName(schema, 'vendorAgreementId').error}
            helperText={getErrorByName(schema, 'vendorAgreementId').message}
            parentTranslationPath={parentTranslationPath}
          />
          <SwitchComponent
            themeClass={'theme-line'}
            isChecked={state.offerWithDuration}
            onChangeHandler={(_e, checked) =>
              dispatch({ id: 'offerWithDuration', value: checked })
            }
            label={'offer_with_duration'}
            labelPlacement={'start'}
            parentTranslationPath={parentTranslationPath}
          />
          <DatePickerComponent
            value={state.offerStartDate}
            labelValue={'start_date'}
            labelClasses='Requierd-Color'
            onDateChanged={(newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  offerStartDate: newValue?.format('YYYY-MM-DD'),
                  offerEndDate: null,
                },
              });
            }}
            error={getErrorByName(schema, 'offerStartDate').error}
            helperText={getErrorByName(schema, 'offerStartDate').message}
            isSubmitted={isSubmitted}
            parentTranslationPath={parentTranslationPath}
          />
          {state.offerWithDuration && (
            <>
              <DatePickerComponent
                value={state.offerEndDate}
                labelValue={'end_date'}
                labelClasses='Requierd-Color'
                onDateChanged={(newValue) => {
                  dispatch({
                    id: 'offerEndDate',
                    value: newValue?.format('YYYY-MM-DD'),
                  });
                }}
                error={getErrorByName(schema, 'offerEndDate').error}
                helperText={getErrorByName(schema, 'offerEndDate').message}
                isSubmitted={isSubmitted}
                parentTranslationPath={parentTranslationPath}
                minDate={
                  moment(state.offerStartDate).isValid() &&
                  moment(state.offerStartDate).format('YYYY-MM-DD')
                }
              />
              <Inputs
                readOnly
                labelValue={'duration'}
                value={state.offerDuration}
                parentTranslationPath={parentTranslationPath}
              />
            </>
          )}
          <Inputs
            labelValue={'description'}
            multiline
            rows={4}
            value={state.description}
            onInputChanged={(e) =>
              dispatch({ id: 'description', value: e.target.value })
            }
            parentTranslationPath={parentTranslationPath}
          />
          <div className='offer-discount-wrapper-dark-bg'>
            <div className='offer-discount-wrapper'>
              <span className='offer-discount-wrapper-header'>
                {t('add-offer-discount')}
              </span>
              <div className='offer-discount-inner-section'>
                <SelectComponet
                  idRef={'selectDiscountTypeRef'}
                  labelValue={'discount_type'}
                  data={[
                    {
                      id: 1,
                      title: 'fixed-amount',
                      value: 2,
                    },
                    { id: 2, title: 'maximum-amount', value: 1 },
                  ]}
                  valueInput={'value'}
                  textInput={'title'}
                  value={state.discountType}
                  onSelectChanged={(newValue) => {
                    dispatch({
                      id: 'discountType',
                      value: +newValue,
                    });
                  }}
                  emptyItem={{
                    value: null,
                    text: 'select_discount_type',
                    isDisabled: true,
                  }}
                  isSubmitted={isSubmitted}
                  parentTranslationPath={parentTranslationPath}
                  isWithError
                  error={getErrorByName(schema, 'discountType').error}
                  helperText={getErrorByName(schema, 'discountType').message}
                />
                <Inputs
                  idRef={'enterDiscountPercentageRef'}
                  labelValue={
                    state?.discountType === 1
                      ? 'discount-up-to'
                      : state?.discountType === 2
                      ? 'discount-fixed-amount'
                      : 'discount'
                  }
                  inputPlaceholder={'eg 15%'}
                  labelClasses='Requierd-Color'
                  value={state.discountPercentage}
                  endAdornment={<span className='p-1'>%</span>}
                  type={'number'}
                  onInputChanged={(e) => {
                    if (e.target.valueAsNumber < 0) {
                      return;
                    }
                    dispatch({
                      id: 'discountPercentage',
                      value: e.target.valueAsNumber,
                    });
                  }}
                  isSubmitted={isSubmitted}
                  isDisabled={!state.discountType}
                  parentTranslationPath={parentTranslationPath}
                  isWithError
                  error={getErrorByName(schema, 'discountPercentage').error}
                  helperText={
                    getErrorByName(schema, 'discountPercentage').message
                  }
                />
              </div>
              <span>
                {state?.discountType === 1
                  ? t(
                      'clients-can-get-a-discount-up-to-this-maximum-amount-percentage-depending-on-their-eligibility-or-promotion-rules'
                    )
                  : state?.discountType === 2
                  ? t(
                      'this-discount-percentage-is-applied-to-all-eligible-transactions'
                    )
                  : ''}
              </span>
            </div>
          </div>
          <SelectComponet
            data={offerAvailabilityOptions}
            labelClasses='Requierd-Color'
            labelValue={'offer_availability'}
            textInput={'title'}
            valueInput={'value'}
            isSubmitted={isSubmitted}
            value={state.offerAvailableOn}
            parentTranslationPath={parentTranslationPath}
            onSelectChanged={(value) => {
              dispatch({
                id: 'offerAvailableOn',
                value,
              });
            }}
            isWithError
            error={getErrorByName(schema, 'offerAvailableOn').error}
            helperText={getErrorByName(schema, 'offerAvailableOn').message}
          />
          <Inputs
            labelValue={'promo_code'}
            value={state.promoCode}
            onInputChanged={(e) =>
              dispatch({ id: 'promoCode', value: e.target.value })
            }
            isWithError
            parentTranslationPath={parentTranslationPath}
          />
          <Inputs
            endAdornment={
              <ImageUploader
                accept={'image/*'}
                uploadedChanged={(files) => {
                  dispatch({ id: 'qrFileId', value: files?.[0]?.uuid });
                }}
                initUploadedFiles={state.files}
                multiple={false}
                isDisabled={!!state.qrCode}
              />
            }
            isDisabled={!!state.qrFileId}
            value={state.qrCode}
            onInputChanged={(e) =>
              dispatch({ id: 'qrCode', value: e.target.value })
            }
            labelValue={'qr_code'}
            parentTranslationPath={parentTranslationPath}
          />
          <LookupAutocomplete
            lookupName={'OfferStatus'}
            label={'offer_status'}
            required
            parentTranslationPath={parentTranslationPath}
            selectedValues={state.selectedStatus}
            isSubmitted={isSubmitted}
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  offerStatusId: newValue?.lookupItemId,
                  selectedStatus: newValue,
                  isPublishOffer:
                    newValue?.lookupItemName?.toLowerCase() === 'active',
                },
              });
            }}
            withError
            error={getErrorByName(schema, 'offerStatusId').error}
            helperText={getErrorByName(schema, 'offerStatusId').message}
          />
          <SwitchComponent
            label={'publish_offer'}
            isChecked={state.isPublishOffer}
            onChangeHandler={(_e, checked) =>
              dispatch({ id: 'isPublishOffer', value: checked })
            }
            themeClass={'theme-inline'}
            parentTranslationPath={parentTranslationPath}
            isDisabled={
              state.selectedStatus?.lookupItemName?.toLowerCase() !== 'active'
            }
          />
        </div>
      }
      onSaveClicked={saveHandler}
    />
  );
};
export default OfferManagementDialog;
